export const advancedFunctionsData = {
  title: "Advanced Functions & Analysis",
  description: "Master polynomial and rational functions, exponential and logarithmic relationships, and function composition techniques essential for calculus readiness.",
  skills: [
    {
      id: "polynomial-rational",
      title: "Polynomial & Rational Functions",
      description: "Analyze polynomial and rational functions including zeros, asymptotes, end behavior, and graphing techniques.",
      explanation: `
## Understanding Polynomial & Rational Functions

### Polynomial Functions
A polynomial function has the form: f(x) = aₙxⁿ + aₙ₋₁xⁿ⁻¹ + ... + a₁x + a₀

**Key Concepts:**
- **Degree**: The highest power of x determines the degree
- **Leading Coefficient**: The coefficient of the highest degree term
- **Zeros/Roots**: Values where f(x) = 0
- **End Behavior**: Determined by degree and leading coefficient
  - Even degree, positive leading coefficient: both ends up
  - Even degree, negative leading coefficient: both ends down
  - Odd degree, positive leading coefficient: left down, right up
  - Odd degree, negative leading coefficient: left up, right down

**Finding Zeros:**
1. Factor the polynomial completely
2. Set each factor equal to zero
3. Solve for x
4. Multiplicity affects the graph at that zero

### Rational Functions
A rational function has the form: f(x) = P(x)/Q(x) where P and Q are polynomials

**Key Features:**
- **Vertical Asymptotes**: Occur where Q(x) = 0 (denominator zeros)
- **Horizontal Asymptotes**: 
  - If degree of P < degree of Q: y = 0
  - If degree of P = degree of Q: y = ratio of leading coefficients
  - If degree of P > degree of Q: no horizontal asymptote (oblique asymptote exists)
- **Holes**: Occur when a factor cancels from numerator and denominator
- **x-intercepts**: Where P(x) = 0 (but not where Q(x) = 0)

**Example:**
For f(x) = (x² - 4)/(x² - x - 6) = (x-2)(x+2)/[(x-3)(x+2)]
- Hole at x = -2 (cancels)
- Vertical asymptote at x = 3
- Horizontal asymptote at y = 1 (same degree, ratio 1/1)
- x-intercept at x = 2
      `,
      questions: [
        {
          id: 1,
          question: "Find all zeros and their multiplicities for f(x) = x⁴ - 5x³ + 6x²",
          options: [
            "x = 0 (multiplicity 2), x = 2, x = 3",
            "x = 0, x = 2 (multiplicity 2), x = 3",
            "x = 0 (multiplicity 2), x = 2 (multiplicity 2)",
            "x = 0, x = 1, x = 2, x = 3"
          ],
          correctAnswer: 0,
          solution: "Factor: f(x) = x²(x² - 5x + 6) = x²(x - 2)(x - 3). Zeros: x = 0 with multiplicity 2 (graph touches x-axis), x = 2 with multiplicity 1 (crosses), x = 3 with multiplicity 1 (crosses)."
        },
        {
          id: 2,
          question: "Determine the end behavior of f(x) = -2x⁵ + 3x³ - x + 7",
          options: [
            "As x → -∞, f(x) → ∞; as x → ∞, f(x) → -∞",
            "As x → -∞, f(x) → -∞; as x → ∞, f(x) → ∞",
            "As x → -∞, f(x) → ∞; as x → ∞, f(x) → ∞",
            "As x → -∞, f(x) → -∞; as x → ∞, f(x) → -∞"
          ],
          correctAnswer: 0,
          solution: "Degree is 5 (odd), leading coefficient is -2 (negative). For odd degree with negative leading coefficient: left end goes up (∞), right end goes down (-∞)."
        },
        {
          id: 3,
          question: "Find the vertical asymptotes of f(x) = (x + 1)/(x² - 9)",
          options: [
            "x = 3 only",
            "x = -3 and x = 3",
            "x = -1",
            "x = 0"
          ],
          correctAnswer: 1,
          solution: "Set denominator equal to zero: x² - 9 = 0, so (x - 3)(x + 3) = 0. Vertical asymptotes at x = 3 and x = -3. The numerator (x + 1) doesn't cancel with denominator."
        },
        {
          id: 4,
          question: "What is the horizontal asymptote of f(x) = (3x² - 2x + 1)/(x² + 4)?",
          options: [
            "y = 3",
            "y = 0",
            "y = 1",
            "No horizontal asymptote"
          ],
          correctAnswer: 0,
          solution: "Both numerator and denominator have degree 2. Horizontal asymptote is the ratio of leading coefficients: y = 3/1 = 3."
        },
        {
          id: 5,
          question: "Identify any holes in f(x) = (x² - 4)/(x² + x - 6)",
          options: [
            "Hole at x = 2",
            "Hole at x = -3",
            "Holes at x = 2 and x = -2",
            "No holes"
          ],
          correctAnswer: 0,
          solution: "Factor: f(x) = (x - 2)(x + 2)/[(x + 3)(x - 2)]. The factor (x - 2) cancels, creating a hole at x = 2. To find y-coordinate: substitute x = 2 into simplified form (x + 2)/(x + 3) = 4/5. Hole at (2, 4/5)."
        },
        {
          id: 6,
          question: "For f(x) = x³ - 4x² + x + 6, how many turning points can the graph have at most?",
          options: [
            "2",
            "3",
            "4",
            "1"
          ],
          correctAnswer: 0,
          solution: "A polynomial of degree n can have at most (n - 1) turning points. Since this is degree 3, maximum turning points = 3 - 1 = 2."
        },
        {
          id: 7,
          question: "Find the x-intercepts of f(x) = (x² - 9)/(x + 1)",
          options: [
            "x = -3 and x = 3",
            "x = -1",
            "x = 3 only",
            "x = 0"
          ],
          correctAnswer: 0,
          solution: "x-intercepts occur where numerator = 0 (and denominator ≠ 0). x² - 9 = 0 gives x = ±3. Check: x = -1 makes denominator zero (vertical asymptote, not intercept). Both x = -3 and x = 3 are valid x-intercepts."
        },
        {
          id: 8,
          question: "What type of asymptote does f(x) = (x³ + 2x)/(x² - 1) have?",
          options: [
            "Oblique (slant) asymptote",
            "Horizontal asymptote",
            "No asymptote",
            "Vertical asymptote only"
          ],
          correctAnswer: 0,
          solution: "Degree of numerator (3) > degree of denominator (2) by exactly 1. This means there's an oblique/slant asymptote. Use polynomial long division to find it: y = x (approximately)."
        },
        {
          id: 9,
          question: "If f(x) = 2x⁴ - 3x² + 1, what is the y-intercept?",
          options: [
            "1",
            "0",
            "-3",
            "2"
          ],
          correctAnswer: 0,
          solution: "The y-intercept occurs when x = 0. f(0) = 2(0)⁴ - 3(0)² + 1 = 1. Y-intercept is (0, 1)."
        },
        {
          id: 10,
          question: "For f(x) = (2x - 6)/(x² - 9), identify all discontinuities",
          options: [
            "Hole at x = 3, vertical asymptote at x = -3",
            "Vertical asymptotes at x = 3 and x = -3",
            "Hole at x = -3, vertical asymptote at x = 3",
            "No discontinuities"
          ],
          correctAnswer: 0,
          solution: "Factor: f(x) = 2(x - 3)/[(x - 3)(x + 3)] = 2/(x + 3) after canceling (x - 3). Hole at x = 3 (canceled factor), vertical asymptote at x = -3 (remaining denominator zero)."
        }
      ]
    },
    {
      id: "exponential-logarithmic",
      title: "Exponential & Logarithmic Functions",
      description: "Explore inverse relationships between exponential and logarithmic functions, including real-world modeling applications.",
      explanation: `
## Exponential & Logarithmic Functions

### Exponential Functions
Form: f(x) = abˣ where a ≠ 0, b > 0, b ≠ 1

**Properties:**
- Domain: all real numbers
- Range: (0, ∞) if a > 0; (-∞, 0) if a < 0
- Horizontal asymptote: y = 0
- If b > 1: exponential growth
- If 0 < b < 1: exponential decay

**Natural Exponential Function:** f(x) = eˣ where e ≈ 2.71828

### Logarithmic Functions
Form: f(x) = logₐ(x) where a > 0, a ≠ 1

**Key Relationship:** y = logₐ(x) if and only if x = aʸ

**Properties:**
- Domain: (0, ∞)
- Range: all real numbers
- Vertical asymptote: x = 0
- Passes through (1, 0) and (a, 1)

**Common Logarithms:**
- log(x) = log₁₀(x) (common logarithm)
- ln(x) = logₑ(x) (natural logarithm)

### Logarithm Properties
1. **Product Rule:** logₐ(xy) = logₐ(x) + logₐ(y)
2. **Quotient Rule:** logₐ(x/y) = logₐ(x) - logₐ(y)
3. **Power Rule:** logₐ(xⁿ) = n·logₐ(x)
4. **Change of Base:** logₐ(x) = log(x)/log(a) = ln(x)/ln(a)
5. **Inverse Property:** logₐ(aˣ) = x and a^(logₐ(x)) = x

### Real-World Applications
- **Compound Interest:** A = P(1 + r/n)^(nt)
- **Continuous Growth:** A = Pe^(rt)
- **Half-life/Decay:** N(t) = N₀(1/2)^(t/h)
- **Population Growth:** P(t) = P₀e^(kt)
      `,
      questions: [
        {
          id: 11,
          question: "Solve for x: 3^(2x-1) = 27",
          options: [
            "x = 2",
            "x = 3",
            "x = 1",
            "x = 4"
          ],
          correctAnswer: 0,
          solution: "Rewrite 27 as 3³: 3^(2x-1) = 3³. Since bases are equal, exponents are equal: 2x - 1 = 3. Solve: 2x = 4, so x = 2."
        },
        {
          id: 12,
          question: "Evaluate log₂(64)",
          options: [
            "6",
            "8",
            "4",
            "32"
          ],
          correctAnswer: 0,
          solution: "log₂(64) asks '2 to what power equals 64?' Since 2⁶ = 64, log₂(64) = 6."
        },
        {
          id: 13,
          question: "Simplify: log₅(25) + log₅(5)",
          options: [
            "3",
            "2",
            "4",
            "5"
          ],
          correctAnswer: 0,
          solution: "Using product rule: log₅(25) + log₅(5) = log₅(25 × 5) = log₅(125). Since 5³ = 125, log₅(125) = 3."
        },
        {
          id: 14,
          question: "Solve: ln(x) + ln(x - 2) = ln(3)",
          options: [
            "x = 3",
            "x = 1",
            "x = 5",
            "x = 2"
          ],
          correctAnswer: 0,
          solution: "Use product rule: ln[x(x - 2)] = ln(3). Therefore x(x - 2) = 3. Expand: x² - 2x - 3 = 0. Factor: (x - 3)(x + 1) = 0. Solutions: x = 3 or x = -1. Check domain: x > 2 (so ln(x - 2) is defined). Only x = 3 is valid."
        },
        {
          id: 15,
          question: "If $5000 is invested at 6% annual interest compounded continuously, what is the amount after 10 years? Use A = Pe^(rt)",
          options: [
            "$9110.59",
            "$8954.24",
            "$10000.00",
            "$8000.00"
          ],
          correctAnswer: 0,
          solution: "Use A = Pe^(rt) where P = 5000, r = 0.06, t = 10. A = 5000e^(0.06×10) = 5000e^0.6 ≈ 5000(1.8221) ≈ $9110.59."
        },
        {
          id: 16,
          question: "Express log₃(81) - log₃(9) as a single logarithm",
          options: [
            "log₃(9)",
            "log₃(72)",
            "log₃(3)",
            "log₃(27)"
          ],
          correctAnswer: 0,
          solution: "Use quotient rule: log₃(81) - log₃(9) = log₃(81/9) = log₃(9). Alternatively, log₃(81) = 4 and log₃(9) = 2, so 4 - 2 = 2 = log₃(9)."
        },
        {
          id: 17,
          question: "Solve for x: e^(2x) = 20",
          options: [
            "x = ln(20)/2",
            "x = 2ln(20)",
            "x = ln(10)",
            "x = 20/2"
          ],
          correctAnswer: 0,
          solution: "Take natural log of both sides: ln(e^(2x)) = ln(20). Using inverse property: 2x = ln(20). Therefore x = ln(20)/2 ≈ 1.4979."
        },
        {
          id: 18,
          question: "Simplify: 3log(x) - log(x²)",
          options: [
            "log(x)",
            "2log(x)",
            "log(x³)",
            "0"
          ],
          correctAnswer: 0,
          solution: "Use power rule: 3log(x) = log(x³) and log(x²) stays as is. Then: log(x³) - log(x²) = log(x³/x²) = log(x)."
        },
        {
          id: 19,
          question: "A radioactive substance has a half-life of 8 years. If you start with 100 grams, how much remains after 24 years?",
          options: [
            "12.5 grams",
            "25 grams",
            "6.25 grams",
            "50 grams"
          ],
          correctAnswer: 0,
          solution: "Use N(t) = N₀(1/2)^(t/h) where N₀ = 100, t = 24, h = 8. N(24) = 100(1/2)^(24/8) = 100(1/2)³ = 100(1/8) = 12.5 grams."
        },
        {
          id: 20,
          question: "Change log₇(49) to base 10",
          options: [
            "log(49)/log(7)",
            "log(7)/log(49)",
            "log(49) × log(7)",
            "log(7 × 49)"
          ],
          correctAnswer: 0,
          solution: "Use change of base formula: logₐ(b) = log(b)/log(a). Therefore log₇(49) = log(49)/log(7) = log(7²)/log(7) = 2log(7)/log(7) = 2."
        }
      ]
    },
    {
      id: "function-composition",
      title: "Function Composition & Inverses",
      description: "Master function composition, determining invertibility, and finding inverse functions through domain restriction.",
      explanation: `
## Function Composition & Inverse Functions

### Function Composition
The composition of functions f and g, denoted (f ∘ g)(x), means f(g(x)).

**Key Points:**
- Read right to left: f(g(x)) means "apply g first, then f"
- Domain of f ∘ g: all x in domain of g such that g(x) is in domain of f
- Generally, f ∘ g ≠ g ∘ f (composition is not commutative)

**Example:**
If f(x) = x² and g(x) = x + 3:
- (f ∘ g)(x) = f(g(x)) = f(x + 3) = (x + 3)²
- (g ∘ f)(x) = g(f(x)) = g(x²) = x² + 3

### Inverse Functions
A function f has an inverse f⁻¹ if and only if f is one-to-one (passes horizontal line test).

**Properties:**
- f(f⁻¹(x)) = x for all x in domain of f⁻¹
- f⁻¹(f(x)) = x for all x in domain of f
- Domain of f = Range of f⁻¹
- Range of f = Domain of f⁻¹
- Graphs of f and f⁻¹ are reflections across y = x

### Finding Inverse Functions
**Steps:**
1. Replace f(x) with y
2. Swap x and y
3. Solve for y
4. Replace y with f⁻¹(x)
5. Verify: check that f(f⁻¹(x)) = x and f⁻¹(f(x)) = x

### Domain Restriction
Some functions are not one-to-one over their entire domain but can be made one-to-one by restricting the domain.

**Example:** f(x) = x²
- Not one-to-one on (-∞, ∞)
- Restrict to [0, ∞): f(x) = x², x ≥ 0
- Now f⁻¹(x) = √x exists

**Common Restrictions:**
- Quadratic functions: restrict to one side of vertex
- Trigonometric functions: restrict to principal domains
- Even functions: restrict to x ≥ 0 or x ≤ 0
      `,
      questions: [
        {
          id: 21,
          question: "If f(x) = 2x + 3 and g(x) = x² - 1, find (f ∘ g)(2)",
          options: [
            "9",
            "11",
            "7",
            "13"
          ],
          correctAnswer: 0,
          solution: "(f ∘ g)(2) = f(g(2)). First find g(2) = 2² - 1 = 3. Then f(3) = 2(3) + 3 = 9."
        },
        {
          id: 22,
          question: "Find the inverse of f(x) = (x - 5)/3",
          options: [
            "f⁻¹(x) = 3x + 5",
            "f⁻¹(x) = 3x - 5",
            "f⁻¹(x) = (x + 5)/3",
            "f⁻¹(x) = 5 - 3x"
          ],
          correctAnswer: 0,
          solution: "Let y = (x - 5)/3. Swap: x = (y - 5)/3. Solve for y: 3x = y - 5, so y = 3x + 5. Therefore f⁻¹(x) = 3x + 5."
        },
        {
          id: 23,
          question: "If f(x) = x³ and g(x) = 2x - 1, find (g ∘ f)(x)",
          options: [
            "2x³ - 1",
            "(2x - 1)³",
            "2x³ + 1",
            "8x³ - 1"
          ],
          correctAnswer: 0,
          solution: "(g ∘ f)(x) = g(f(x)) = g(x³) = 2(x³) - 1 = 2x³ - 1."
        },
        {
          id: 24,
          question: "Which function has an inverse without domain restriction?",
          options: [
            "f(x) = x³ + 2",
            "f(x) = x² + 1",
            "f(x) = |x|",
            "f(x) = x⁴"
          ],
          correctAnswer: 0,
          solution: "f(x) = x³ + 2 is one-to-one (passes horizontal line test) because x³ is strictly increasing. The others are not one-to-one: x², |x|, and x⁴ all fail the horizontal line test."
        },
        {
          id: 25,
          question: "If f(x) = √(x + 4), what is the domain of f⁻¹(x)?",
          options: [
            "[0, ∞)",
            "[-4, ∞)",
            "(-∞, ∞)",
            "[4, ∞)"
          ],
          correctAnswer: 0,
          solution: "Domain of f⁻¹ = Range of f. Since f(x) = √(x + 4), the range is [0, ∞) (square root outputs non-negative values). Therefore domain of f⁻¹ is [0, ∞)."
        },
        {
          id: 26,
          question: "Find (f ∘ f)(x) if f(x) = 1/x",
          options: [
            "x",
            "1/x²",
            "2/x",
            "1"
          ],
          correctAnswer: 0,
          solution: "(f ∘ f)(x) = f(f(x)) = f(1/x) = 1/(1/x) = x. This shows f is its own inverse!"
        },
        {
          id: 27,
          question: "For f(x) = x² with domain restricted to x ≥ 0, find f⁻¹(16)",
          options: [
            "4",
            "16",
            "256",
            "±4"
          ],
          correctAnswer: 0,
          solution: "With domain x ≥ 0, f⁻¹(x) = √x. Therefore f⁻¹(16) = √16 = 4. (We don't use -4 because of the domain restriction.)"
        },
        {
          id: 28,
          question: "If f(x) = 2x - 7 and (f ∘ g)(x) = 4x + 1, find g(x)",
          options: [
            "g(x) = 2x + 4",
            "g(x) = 2x - 3",
            "g(x) = 4x + 8",
            "g(x) = x + 4"
          ],
          correctAnswer: 0,
          solution: "(f ∘ g)(x) = f(g(x)) = 2g(x) - 7 = 4x + 1. Solve: 2g(x) = 4x + 8, so g(x) = 2x + 4."
        },
        {
          id: 29,
          question: "Find the inverse of f(x) = (2x + 1)/(x - 3)",
          options: [
            "f⁻¹(x) = (3x + 1)/(x - 2)",
            "f⁻¹(x) = (x - 3)/(2x + 1)",
            "f⁻¹(x) = (3x - 1)/(x + 2)",
            "f⁻¹(x) = (2x - 1)/(x + 3)"
          ],
          correctAnswer: 0,
          solution: "Let y = (2x + 1)/(x - 3). Swap: x = (2y + 1)/(y - 3). Cross multiply: x(y - 3) = 2y + 1. Expand: xy - 3x = 2y + 1. Collect y terms: xy - 2y = 3x + 1. Factor: y(x - 2) = 3x + 1. Solve: y = (3x + 1)/(x - 2). Therefore f⁻¹(x) = (3x + 1)/(x - 2)."
        },
        {
          id: 30,
          question: "If f and g are inverse functions and f(3) = 7, what is g(7)?",
          options: [
            "3",
            "7",
            "10",
            "21"
          ],
          correctAnswer: 0,
          solution: "Since f and g are inverses, g(f(x)) = x for all x. Given f(3) = 7, we have g(f(3)) = g(7) = 3."
        }
      ]
    }
  ]
};